import React, { useState } from "react";
import { Box, Grid, Tab } from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import dayjs from "dayjs";
import { IEntryExitDashboardFilter } from "../api/types";
import {
  useGetEntryExitSummary,
  useGetEntryExitCharts,
} from "../api/hooks";
import FilterBar from "./FilterBar";
import KpiSummaryCards from "./KpiSummaryCards";
import TrendChart from "./TrendChart";
import MemberTypePieChart from "./MemberTypePieChart";
import DepartmentBarChart from "./DepartmentBarChart";
import CurrentlyInsideTable from "./CurrentlyInsideTable";
import VisitorLogsTable from "./VisitorLogsTable";

export default function EntryExitDashboard() {
  const todayStr = dayjs().format("YYYY-MM-DD");

  const [filter, setFilter] = useState<IEntryExitDashboardFilter>({
    fromDate: todayStr,
    toDate: todayStr,
    memberTypeID: null,
    instituteID: null,
    departmentID: null,
    programID: null,
  });

  const [activeTab, setActiveTab] = useState<string>("1");

  const isFiltered = React.useMemo(() => {
    return (
      filter.fromDate !== todayStr ||
      filter.toDate !== todayStr ||
      !!filter.memberTypeID ||
      !!filter.instituteID ||
      !!filter.departmentID ||
      !!filter.programID
    );
  }, [filter, todayStr]);

  const isSingleDay = React.useMemo(() => {
    if (!filter.fromDate || !filter.toDate) return true;
    return filter.fromDate === filter.toDate;
  }, [filter.fromDate, filter.toDate]);

  const {
    data: summaryData,
    isLoading: isSummaryLoading,
  } = useGetEntryExitSummary(filter);

  const {
    data: chartsData,
    isLoading: isChartsLoading,
  } = useGetEntryExitCharts(filter);

  const handleFilterChange = (newFilter: IEntryExitDashboardFilter) => {
    setFilter(newFilter);
  };

  const handleReset = () => {
    setFilter({
      fromDate: todayStr,
      toDate: todayStr,
      memberTypeID: null,
      instituteID: null,
      departmentID: null,
      programID: null,
    });
  };

  return (
    <Box sx={{ width: "100%", typography: "body1" }}>
      {/* Filter Section */}
      <FilterBar
        filter={filter}
        onFilterChange={handleFilterChange}
        onReset={handleReset}
      />

      {/* Top KPI Cards */}
      <KpiSummaryCards
        summary={summaryData?.apiData}
        isLoading={isSummaryLoading}
        isFiltered={isFiltered}
      />

      {/* Visual Charts Grid */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} lg={7}>
          <TrendChart
            data={chartsData?.apiData?.trend}
            isLoading={isChartsLoading}
            isSingleDay={isSingleDay}
          />
        </Grid>

        <Grid item xs={12} lg={5}>
          <MemberTypePieChart
            data={chartsData?.apiData?.memberTypeDistribution}
            isLoading={isChartsLoading}
          />
        </Grid>

        <Grid item xs={12}>
          <DepartmentBarChart
            data={chartsData?.apiData?.departmentDistribution}
            isLoading={isChartsLoading}
          />
        </Grid>
      </Grid>

      {/* Data Tables in TabContext */}
      <TabContext value={activeTab}>
        <Box sx={{ mb: 1 }}>
          <TabList
            onChange={(_, val) => setActiveTab(val)}
            aria-label="Entry exit data tables"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ marginBlock: "0em" }}
          >
            <Tab
              sx={{
                backgroundColor: "white",
                marginInline: { xs: "2px", sm: "4px" },
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                minHeight: { xs: 40, sm: 48 },
              }}
              label={
                summaryData?.apiData?.currentlyInLibrary !== undefined
                  ? `Currently In Library (${summaryData.apiData.currentlyInLibrary})`
                  : "Currently In Library"
              }
              value="1"
            />
            <Tab
              sx={{
                backgroundColor: "white",
                marginInline: { xs: "2px", sm: "4px" },
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                minHeight: { xs: 40, sm: 48 },
              }}
              label="Visitor Activity Logs"
              value="2"
            />
          </TabList>
        </Box>

        <TabPanel value="1" sx={{ p: 0 }}>
          <CurrentlyInsideTable filter={filter} />
        </TabPanel>
        <TabPanel value="2" sx={{ p: 0 }}>
          <VisitorLogsTable filter={filter} />
        </TabPanel>
      </TabContext>
    </Box>
  );
}
